import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#0B0B10] text-white">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-violet-600/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-fuchsia-600/10 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-16">
        {/* Back button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-violet-600/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-violet-400" />
            </div>
            <h1 className="text-3xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-zinc-400">Last updated: February 2025</p>
        </motion.div>

        {/* Content */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="prose prose-invert prose-zinc max-w-none"
        >
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">1. Information We Collect</h2>
            <p className="text-zinc-400 leading-relaxed mb-4">We collect the following types of information:</p>
            <ul className="list-disc list-inside text-zinc-400 space-y-2">
              <li>Telegram user ID and username</li>
              <li>Conversation history with AI companions</li>
              <li>Payment information (processed securely via Stripe)</li>
              <li>Language preferences</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">2. How We Use Your Information</h2>
            <p className="text-zinc-400 leading-relaxed mb-4">Your information is used to:</p>
            <ul className="list-disc list-inside text-zinc-400 space-y-2">
              <li>Provide and personalize the AI companion experience</li>
              <li>Process payments and manage subscriptions</li>
              <li>Improve our Service and AI responses</li>
              <li>Send important service notifications</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">3. Data Security</h2>
            <p className="text-zinc-400 leading-relaxed">
              We implement industry-standard security measures to protect your data. All communications 
              through Telegram are encrypted. Payment processing is handled securely by Stripe, and we 
              never store your full credit card information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">4. Data Retention</h2>
            <p className="text-zinc-400 leading-relaxed">
              Conversation history is retained to provide continuity in your interactions with AI companions. 
              You may request deletion of your data at any time by contacting us through the Telegram bot.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">5. Third-Party Services</h2>
            <p className="text-zinc-400 leading-relaxed mb-4">We use the following third-party services:</p>
            <ul className="list-disc list-inside text-zinc-400 space-y-2">
              <li><strong className="text-white">Telegram</strong> - For messaging platform</li>
              <li><strong className="text-white">Stripe</strong> - For payment processing</li>
              <li><strong className="text-white">OpenAI</strong> - For AI conversation generation</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">6. Your Rights</h2>
            <p className="text-zinc-400 leading-relaxed mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-zinc-400 space-y-2">
              <li>Access your personal data</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Withdraw consent at any time</li>
              <li>Lodge a complaint with a supervisory authority</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">7. No Public Sharing</h2>
            <p className="text-zinc-400 leading-relaxed">
              We do not share, sell, or rent your personal information to third parties for marketing purposes. 
              Your conversations remain private and are never exposed publicly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">8. Cookies</h2>
            <p className="text-zinc-400 leading-relaxed">
              Our website uses minimal cookies for essential functionality only, such as remembering your 
              age verification status. We do not use tracking cookies for advertising.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">9. Changes to This Policy</h2>
            <p className="text-zinc-400 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any significant 
              changes through our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">10. Contact Us</h2>
            <p className="text-zinc-400 leading-relaxed">
              For any privacy-related questions or requests, please contact us through our Telegram bot.
            </p>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
